import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescribedField from '../DescribedField';
import LogoUploadField from '../LogoUploadField';

const emptyImage = () => ({
  id: crypto.randomUUID(),
  url: '',
  caption: '',
});

/**
 * GalleryBlockEditor — Editor do bloco Galeria
 * TODO 4.13
 *
 * Props:
 *   - value: { images: [{ id, url, caption }] }
 *   - onChange: (patch) => void
 *   - context: object
 */
const GalleryBlockEditor = ({ value = {}, onChange }) => {
  const { images = [] } = value;

  const patchImages = (newImages) => onChange({ ...value, images: newImages });

  const addImage = () => patchImages([...images, emptyImage()]);

  const removeImage = (index) => patchImages(images.filter((_, i) => i !== index));

  const updateImage = (index, field, val) => {
    const updated = images.map((img, i) =>
      i === index ? { ...img, [field]: val } : img
    );
    patchImages(updated);
  };

  return (
    <DescribedField
      label="Imagens da galeria"
      description="Grade de imagens do ambiente, trabalhos realizados, equipe ou antes/depois. Peso visual forte — use poucas imagens de boa qualidade. Recomendado até 10 imagens com resolução mínima de 1024px no lado maior. Legenda é opcional."
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {images.map((img, index) => (
          <Paper key={img.id ?? index} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Imagem {index + 1}</Typography>
              <IconButton
                size="small"
                onClick={() => removeImage(index)}
                aria-label="Remover imagem"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ mb: 1.5 }}>
              <LogoUploadField
                logoUrl={img.url}
                onChange={(url) => updateImage(index, 'url', url)}
              />
            </Box>
            <TextField
              fullWidth
              label="Legenda (opcional)"
              value={img.caption}
              onChange={(e) => updateImage(index, 'caption', e.target.value)}
              size="small"
              helperText="Legenda curta exibida abaixo da imagem. Opcional."
            />
          </Paper>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addImage}
          variant="outlined"
          size="small"
          disabled={images.length >= 10}
          sx={{ alignSelf: 'flex-start' }}
        >
          Adicionar imagem
        </Button>
        {images.length >= 10 && (
          <Typography variant="caption" color="text.secondary">
            Limite de 10 imagens atingido.
          </Typography>
        )}
      </Box>
    </DescribedField>
  );
};

GalleryBlockEditor.propTypes = {
  value: PropTypes.shape({
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        caption: PropTypes.string,
      })
    ),
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.object,
};

export default GalleryBlockEditor;
